'use client';

import type { ComponentType, ReactNode } from 'react';
import { useContext } from 'react';

import { UserProfileContext } from '../user-profile/context';
import { UserProfileProvider } from '../user-profile/provider';
import { AuthContext } from './context';
import { AuthProvider } from './provider';

interface WithClientAuthOptions {
  loader?: ReactNode;
}

/**
 * HOC that wraps a component with AuthProvider + UserProfileProvider,
 * but only if those providers aren't already in the tree. This lets
 * lazily-loaded client components that need auth work both inside
 * authenticated layouts (providers already exist) and outside them
 * (e.g. public listing pages where providers are absent).
 *
 * `options.loader` is reserved for future use (e.g. skeleton while auth
 * resolves) but is not applied here to avoid SSR hydration mismatches.
 */
export function withClientAuth<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithClientAuthOptions = {},
): ComponentType<P> {
  void options;

  function WithClientAuth(props: P) {
    const authCtx = useContext(AuthContext);
    const profileCtx = useContext(UserProfileContext);

    let content = <WrappedComponent {...props} />;

    if (profileCtx == null) {
      content = <UserProfileProvider>{content}</UserProfileProvider>;
    }
    if (authCtx == null) {
      content = <AuthProvider>{content}</AuthProvider>;
    }

    return content;
  }

  WithClientAuth.displayName = `withClientAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithClientAuth;
}
